import type { default as RibbonButton, RibbonDropDownProps } from './buttons/RibbonButton';
import * as React from 'react';
import PickerInput, { PickerInputHandle } from './PickerInput';
import createLivePreviewHandler from 'owa-editor-live-preview/lib/utils/createLivePreviewHandler';
import updateSecondClassState from 'owa-editor-ribbonplugin-store/lib/actions/updateSecondClassState';
import { callEditorApi } from 'owa-editor/lib/utils/callEditorApi';
import { FontPickerViewState, FontPicker } from 'owa-editor-ribbon-secondary-ui';
import type { RibbonFormatState } from 'owa-editor-ribbonplugin-store';
import type LivePreviewHandler from 'owa-editor-live-preview/lib/schema/LivePreviewHandler';
import type { EditorViewState } from 'owa-editor';
import { FontPickerItem } from 'owa-format-options/lib/utils/FontPickerItem';

const DROP_DOWN_WIDTH = 30;

export default abstract class PickerInputButtonBase implements RibbonButton {
    public imageSrc = null;
    protected editorViewState: EditorViewState;
    protected formatViewState: RibbonFormatState;
    private livePreview: LivePreviewHandler<string>;
    private pickerInput: PickerInputHandle;

    constructor(
        public key: string,
        private titleStr: string,
        private viewState: FontPickerViewState,
        private inputWidth: number,
        private maxLength: number,
        private autoComplete: boolean,
        private itemStyle?: (item: FontPickerItem) => React.CSSProperties
    ) {}

    abstract onChange(newValue: string, isSubmit: boolean): void;
    abstract getCurrentValue(): string;
    abstract normalizeValue(value: string): string;
    abstract updatePickerItems: (targetWindow: Window) => void;

    protected getPickerStyle = (displayValue: string): React.CSSProperties => {
        return null;
    };

    get width() {
        return this.inputWidth + DROP_DOWN_WIDTH;
    }

    // Empty onExecute causes the button to be rendered with a picker
    onExecute() {}

    title = () => {
        return this.titleStr;
    };

    additionalRenderer = (editorViewState: EditorViewState, formatViewState: RibbonFormatState) => {
        this.editorViewState = editorViewState;
        this.formatViewState = formatViewState;
        this.livePreview =
            this.livePreview ||
            createLivePreviewHandler(editorViewState, value =>
                this.onChange(value, false /*isSubmit*/)
            );

        return (
            <PickerInput
                ref={this.pickerInputRefCallback}
                currentValue={this.getCurrentValue()}
                width={this.inputWidth}
                maxLength={this.maxLength}
                onSubmit={this.onInputSubmit}
                autoComplete={this.autoComplete}
                viewState={this.viewState}
                inputStyle={this.getPickerStyle}
            />
        );
    };

    createDropDownCallback = ({ target, dismiss }: RibbonDropDownProps) => {
        this.updatePickerItems(target.ownerDocument.defaultView);
        this.pickerInput?.focus();

        return (
            <FontPicker
                menuTargetElement={target}
                onDismissMenu={dismiss}
                onSelect={this.onSubmit}
                selectedValue={this.getCurrentValue()}
                livePreview={this.livePreview}
                viewState={this.viewState}
                itemStyle={this.itemStyle}
                shouldFocusOnMount={false}
            />
        );
    };

    private onInputSubmit = (value: string) => {
        this.livePreview?.stopPreview?.();
        this.onSubmit(this.normalizeValue(value));
    };

    private onSubmit = (value: string) => {
        this.onChange(value, true /*isSubmit*/);
        updateSecondClassState(
            this.formatViewState,
            callEditorApi(this.editorViewState, 'getStyleBasedFormatState')
        );
    };

    private pickerInputRefCallback = (ref: PickerInputHandle) => {
        this.pickerInput = ref;
    };
}
